import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Ban, KeyRound, LogOut, PauseCircle, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import { api, ensureOk, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { PermissionGate } from '@/auth/PermissionGate';
import { useCan } from '@/auth/queries';
import { useStepUp } from '@/auth/StepUp';
import { Campo } from '@/components/Campo';
import { ConfirmarAccion } from '@/components/ConfirmarAccion';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { Modal } from '@/components/Modal';
import { fechaHora, fechaLarga, mxn } from '@/lib/formato';
import { cn } from '@/lib/utils';

import { BorrarHistorial } from './BorrarHistorial';
import { CambiarContrasena } from './CambiarContrasena';
import { FormLicencia, licenciaVacia } from './FormLicencia';
import {
  claveAlumno,
  useAlumno,
  useLicenciasAlumno,
  usePagosAlumno,
  useSesionesAlumno,
  type AlumnoDetalle,
  type LicenciaAlumno,
} from './queries';

const PESTANAS = [
  ['datos', 'Datos'],
  ['licencias', 'Licencias'],
  ['pagos', 'Pagos'],
  ['sesiones', 'Sesiones'],
  ['cuenta', 'Estado de la cuenta'],
] as const;
type Pestana = (typeof PESTANAS)[number][0];

/** Invalida la ficha y la lista del directorio tras un cambio. */
function useRefrescar(id: number) {
  const qc = useQueryClient();
  return (detalle?: AlumnoDetalle) => {
    if (detalle) qc.setQueryData(claveAlumno(id), detalle);
    void qc.invalidateQueries({ queryKey: claveAlumno(id) });
    void qc.invalidateQueries({ queryKey: ['alumnos', 'lista'] });
  };
}

// ------------------------------------------------------------------ datos

const datosSchema = z.object({
  name: z.string().trim().min(2, 'Escribe el nombre').max(100),
  email: z.string().trim().email('Escribe un correo válido').max(120),
  isActive: z.boolean(),
  phone: z.string().trim().max(20),
  gender: z.enum(['', 'M', 'H', 'N', 'S']),
  targetSpecialty: z.string().trim().max(80),
  schoolName: z.string().trim().max(150),
  grade: z.string().regex(/^(\d{1,3}(\.\d{1,2})?)?$/, 'Número inválido'),
  enarmAttempts: z.string().regex(/^\d{0,2}$/, 'Número inválido'),
});
type Datos = z.infer<typeof datosSchema>;

function TabDatos({ a, editable }: { a: AlumnoDetalle; editable: boolean }) {
  const refrescar = useRefrescar(a.id);
  const form = useForm<Datos>({
    resolver: zodResolver(datosSchema),
    defaultValues: {
      name: a.name,
      email: a.email,
      isActive: a.isActive,
      phone: a.profile.phone ?? '',
      gender: (a.profile.gender ?? '') as Datos['gender'],
      targetSpecialty: a.profile.targetSpecialty ?? '',
      schoolName: a.profile.schoolName ?? '',
      grade: a.profile.grade !== null ? String(a.profile.grade) : '',
      enarmAttempts: a.profile.enarmAttempts !== null ? String(a.profile.enarmAttempts) : '',
    },
  });
  const guardar = useMutation({
    mutationFn: (v: Datos) =>
      unwrap<AlumnoDetalle>(
        api.PATCH('/api/v1/admin/users/{id}', {
          params: { path: { id: String(a.id) } },
          body: {
            name: v.name,
            email: v.email,
            isActive: v.isActive,
            profile: {
              phone: v.phone || null,
              gender: v.gender || null,
              targetSpecialty: v.targetSpecialty || null,
              schoolName: v.schoolName || null,
              grade: v.grade === '' ? null : Number(v.grade),
              enarmAttempts: v.enarmAttempts === '' ? null : Number(v.enarmAttempts),
            },
          },
        }),
      ),
    onSuccess: (d) => {
      refrescar(d);
      toast.success('Datos guardados.');
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  const { errors } = form.formState;
  const solo = !editable;
  return (
    <form
      className="grid gap-6 xl:grid-cols-2"
      onSubmit={(e) => void form.handleSubmit((v) => guardar.mutate(v))(e)}
      noValidate
    >
      <section className="panel-vidrio space-y-4">
        <h2 className="text-lg font-extrabold">Cuenta</h2>
        <Campo etiqueta="Nombre" error={errors.name?.message}>
          <input className="input-vidrio" disabled={solo} {...form.register('name')} />
        </Campo>
        <Campo etiqueta="Correo" error={errors.email?.message}>
          <input
            type="email"
            className="input-vidrio"
            disabled={solo}
            {...form.register('email')}
          />
        </Campo>
        <label className="flex items-center gap-2 text-sm font-semibold">
          <input
            type="checkbox"
            className="h-4 w-4 accent-[var(--color-azul-600)]"
            disabled={solo}
            {...form.register('isActive')}
          />
          Cuenta activa (desactivarla cierra sus sesiones)
        </label>
        <dl className="grid grid-cols-2 gap-2 text-sm">
          <dt className="text-texto-3">Registro</dt>
          <dd>{fechaHora(a.createdAt)}</dd>
          <dt className="text-texto-3">Último acceso</dt>
          <dd>{fechaHora(a.lastLoginAt)}</dd>
          <dt className="text-texto-3">Correo verificado</dt>
          <dd>{a.emailVerifiedAt ? fechaHora(a.emailVerifiedAt) : 'No'}</dd>
          <dt className="text-texto-3">Contraseña</dt>
          <dd>{a.hasPassword ? 'Definida' : 'Pendiente (no ha usado su enlace)'}</dd>
        </dl>
        {editable && <CambiarContrasena a={a} />}
      </section>
      <section className="panel-vidrio space-y-4">
        <h2 className="text-lg font-extrabold">Perfil</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <Campo etiqueta="Teléfono" error={errors.phone?.message}>
            <input className="input-vidrio" disabled={solo} {...form.register('phone')} />
          </Campo>
          <Campo etiqueta="Género">
            <select className="input-vidrio" disabled={solo} {...form.register('gender')}>
              <option value="">Sin especificar</option>
              <option value="M">Mujer</option>
              <option value="H">Hombre</option>
              <option value="N">No binario</option>
            </select>
          </Campo>
          <Campo etiqueta="Especialidad objetivo">
            <input className="input-vidrio" disabled={solo} {...form.register('targetSpecialty')} />
          </Campo>
          <Campo etiqueta="Escuela">
            <input className="input-vidrio" disabled={solo} {...form.register('schoolName')} />
          </Campo>
          <Campo etiqueta="Promedio" error={errors.grade?.message}>
            <input
              className="input-vidrio"
              inputMode="decimal"
              disabled={solo}
              {...form.register('grade')}
            />
          </Campo>
          <Campo etiqueta="Intentos de ENARM" error={errors.enarmAttempts?.message}>
            <input
              className="input-vidrio"
              inputMode="numeric"
              disabled={solo}
              {...form.register('enarmAttempts')}
            />
          </Campo>
        </div>
        <dl className="grid grid-cols-2 gap-2 text-sm">
          <dt className="text-texto-3">Universidad</dt>
          <dd>{a.profile.university ?? '—'}</dd>
          <dt className="text-texto-3">Lugar</dt>
          <dd>{[a.profile.state, a.profile.country].filter(Boolean).join(', ') || '—'}</dd>
          <dt className="text-texto-3">¿Cómo se enteró?</dt>
          <dd>{a.profile.heardAbout ?? '—'}</dd>
        </dl>
      </section>
      {editable && (
        <div className="xl:col-span-2">
          <button type="submit" className="btn-cs btn-primario" disabled={guardar.isPending}>
            {guardar.isPending ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      )}
    </form>
  );
}

// ------------------------------------------------------------------ licencias

const ESTADO_LICENCIA: Record<LicenciaAlumno['status'], [string, string]> = {
  active: ['Vigente', 'insignia-activa'],
  scheduled: ['Programada', ''],
  expired: ['Vencida', ''],
  revoked: ['Revocada', 'insignia-exclusivo'],
};

function TabLicencias({ a, editable }: { a: AlumnoDetalle; editable: boolean }) {
  const q = useLicenciasAlumno(a.id);
  const refrescar = useRefrescar(a.id);
  const [otorgando, setOtorgando] = useState(false);
  const [nueva, setNueva] = useState(licenciaVacia);
  const [editando, setEditando] = useState<LicenciaAlumno | null>(null);
  const [fechas, setFechas] = useState({ startsOn: '', endsOn: '' });
  const [revocando, setRevocando] = useState<LicenciaAlumno | null>(null);

  const otorgar = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/v1/admin/users/{id}/memberships', {
          params: { path: { id: String(a.id) } },
          body: nueva,
        }),
      ),
    onSuccess: () => {
      refrescar();
      setOtorgando(false);
      setNueva(licenciaVacia());
      toast.success('Licencia otorgada.');
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  const cambiarFechas = useMutation({
    mutationFn: () =>
      unwrap(
        api.PATCH('/api/v1/admin/users/{id}/memberships/{mid}', {
          params: { path: { id: String(a.id), mid: String(editando?.id) } },
          body: fechas,
        }),
      ),
    onSuccess: () => {
      refrescar();
      setEditando(null);
      toast.success('Fechas actualizadas.');
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  const revocar = useMutation({
    mutationFn: (reason: string) =>
      unwrap(
        api.POST('/api/v1/admin/users/{id}/memberships/{mid}/revoke', {
          params: { path: { id: String(a.id), mid: String(revocando?.id) } },
          body: { reason },
        }),
      ),
    onSuccess: () => {
      refrescar();
      setRevocando(null);
      toast.success('Licencia revocada.');
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });

  return (
    <section className="panel-vidrio space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-extrabold">Licencias</h2>
        {editable && (
          <button type="button" className="btn-cs btn-primario" onClick={() => setOtorgando(true)}>
            <Plus size={16} aria-hidden /> Otorgar licencia
          </button>
        )}
      </div>
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data?.length === 0 && <Vacio titulo="Sin licencias" />}
      {q.data && q.data.length > 0 && (
        <div className="overflow-x-auto">
          <table className="tabla-cs w-full text-sm">
            <thead>
              <tr>
                <th>Licencia</th>
                <th>Vigencia</th>
                <th>Estado</th>
                <th>Pago</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.data.map((l) => {
                const [texto, clase] = ESTADO_LICENCIA[l.status];
                return (
                  <tr key={l.id} className={l.status === 'revoked' ? 'opacity-60' : ''}>
                    <td className="font-semibold">
                      {l.name} {l.isGift && <span className="insignia ml-1">Obsequio</span>}
                    </td>
                    <td className="whitespace-nowrap">
                      {fechaLarga(l.startsOn)} – {fechaLarga(l.endsOn)}
                    </td>
                    <td>
                      <span className={cn('insignia', clase)}>{texto}</span>
                      {l.revokeReason && (
                        <p className="mt-1 text-xs text-texto-3">{l.revokeReason}</p>
                      )}
                    </td>
                    <td className="whitespace-nowrap">
                      {l.payment
                        ? `${mxn(l.payment.finalPrice)} · ${l.payment.providerLabel}`
                        : '—'}
                    </td>
                    <td className="space-x-2 text-right whitespace-nowrap">
                      {editable && l.status !== 'revoked' && (
                        <>
                          <button
                            type="button"
                            className="btn-cs btn-contorno px-2 py-1 text-xs"
                            onClick={() => {
                              setFechas({ startsOn: l.startsOn, endsOn: l.endsOn });
                              setEditando(l);
                            }}
                          >
                            Fechas
                          </button>
                          <button
                            type="button"
                            className="btn-cs btn-peligro px-2 py-1 text-xs"
                            onClick={() => setRevocando(l)}
                          >
                            Revocar
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        abierto={otorgando}
        onCerrar={() => setOtorgando(false)}
        titulo="Otorgar licencia"
        ancho="max-w-2xl"
      >
        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (!nueva.membershipId || !nueva.endsOn) {
              toast.error('Elige la licencia y su fecha final.');
              return;
            }
            otorgar.mutate();
          }}
        >
          <FormLicencia valor={nueva} onCambio={setNueva} conAviso />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-cs btn-contorno"
              onClick={() => setOtorgando(false)}
            >
              Cancelar
            </button>
            <button type="submit" className="btn-cs btn-primario" disabled={otorgar.isPending}>
              {otorgar.isPending ? 'Otorgando…' : 'Otorgar'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        abierto={editando !== null}
        onCerrar={() => setEditando(null)}
        titulo={`Fechas de ${editando?.name ?? ''}`}
      >
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            cambiarFechas.mutate();
          }}
        >
          <div className="grid grid-cols-2 gap-3">
            <Campo etiqueta="Desde">
              <input
                type="date"
                className="input-vidrio"
                value={fechas.startsOn}
                onChange={(e) => setFechas((f) => ({ ...f, startsOn: e.target.value }))}
              />
            </Campo>
            <Campo etiqueta="Hasta">
              <input
                type="date"
                className="input-vidrio"
                value={fechas.endsOn}
                min={fechas.startsOn}
                onChange={(e) => setFechas((f) => ({ ...f, endsOn: e.target.value }))}
              />
            </Campo>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-cs btn-contorno" onClick={() => setEditando(null)}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-cs btn-primario"
              disabled={cambiarFechas.isPending}
            >
              Guardar
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmarAccion
        abierto={revocando !== null}
        onCerrar={() => setRevocando(null)}
        titulo={`Revocar ${revocando?.name ?? ''}`}
        descripcion="El alumno pierde el acceso de inmediato. La licencia queda en el historial como revocada."
        textoBoton="Revocar"
        peligro
        pedirMotivo="Motivo"
        ocupado={revocar.isPending}
        onConfirmar={(m) => revocar.mutate(m)}
      />
    </section>
  );
}

// ------------------------------------------------------------------ pagos y sesiones

function TabPagos({ a }: { a: AlumnoDetalle }) {
  const q = usePagosAlumno(a.id);
  return (
    <section className="panel-vidrio space-y-4">
      <h2 className="text-lg font-extrabold">Pagos</h2>
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data?.length === 0 && <Vacio titulo="Sin pagos registrados" />}
      {q.data && q.data.length > 0 && (
        <div className="overflow-x-auto">
          <table className="tabla-cs w-full text-sm">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Licencia</th>
                <th>Forma de pago</th>
                <th>Precio</th>
                <th>Pagado</th>
                <th>Cupón</th>
                <th>Estado</th>
                <th>Folio</th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((p) => (
                <tr key={p.id}>
                  <td className="whitespace-nowrap">{fechaHora(p.paidAt)}</td>
                  <td>{p.membershipName}</td>
                  <td>{p.providerLabel}</td>
                  <td className="tabular">{mxn(p.regularPrice)}</td>
                  <td className="tabular font-bold">{mxn(p.finalPrice)}</td>
                  <td>{p.couponCode ?? '—'}</td>
                  <td>{p.status}</td>
                  <td className="font-mono text-xs">{p.folio ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function TabSesiones({ a, editable }: { a: AlumnoDetalle; editable: boolean }) {
  const q = useSesionesAlumno(a.id);
  const refrescar = useRefrescar(a.id);
  const cerrar = useMutation({
    mutationFn: () =>
      ensureOk(
        api.DELETE('/api/v1/admin/users/{id}/sessions', { params: { path: { id: String(a.id) } } }),
      ),
    onSuccess: () => {
      refrescar();
      toast.success('Sesiones cerradas.');
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  return (
    <section className="panel-vidrio space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-extrabold">Sesiones abiertas</h2>
        {editable && (q.data?.length ?? 0) > 0 && (
          <button
            type="button"
            className="btn-cs btn-contorno"
            onClick={() => cerrar.mutate()}
            disabled={cerrar.isPending}
          >
            <LogOut size={16} aria-hidden /> Cerrar todas
          </button>
        )}
      </div>
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data?.length === 0 && <Vacio titulo="Sin sesiones abiertas" />}
      <ul className="divide-y divide-[var(--separador)]">
        {q.data?.map((s) => (
          <li key={s.id} className="py-3">
            <p className="font-semibold">
              {s.deviceName ?? 'Dispositivo'} · {s.client === 'flutter' ? 'App' : 'Web'}
            </p>
            <p className="text-xs text-texto-3">
              IP {s.ip ?? '—'} · desde {fechaHora(s.createdAt)} · última actividad{' '}
              {fechaHora(s.lastSeenAt)}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ------------------------------------------------------------------ estado de la cuenta

function TabCuenta({ a, editable }: { a: AlumnoDetalle; editable: boolean }) {
  const refrescar = useRefrescar(a.id);
  const navigate = useNavigate();
  const { proteger, dialogo } = useStepUp();
  const [suspender, setSuspender] = useState(false);
  const [susp, setSusp] = useState({
    reason: 'Falta de pago',
    amountDue: '',
    dueOn: '',
    notifyByEmail: true,
  });
  const [bloquear, setBloquear] = useState(false);
  const [borrar, setBorrar] = useState(false);
  const id = { params: { path: { id: String(a.id) } } };

  const accion = (fn: () => Promise<AlumnoDetalle | void>, ok: string, despues?: () => void) => ({
    mutationFn: fn,
    onSuccess: (d: AlumnoDetalle | void) => {
      refrescar(d ?? undefined);
      despues?.();
      toast.success(ok);
    },
    onError: (e: unknown) => toast.error(mensajeDe(e)),
  });

  const suspenderM = useMutation(
    accion(
      () =>
        unwrap<AlumnoDetalle>(
          api.POST('/api/v1/admin/users/{id}/suspension', {
            ...id,
            body: {
              reason: susp.reason,
              amountDue: susp.amountDue || null,
              dueOn: susp.dueOn || null,
              notifyByEmail: susp.notifyByEmail,
            },
          }),
        ),
      'Cuenta suspendida.',
      () => setSuspender(false),
    ),
  );
  const reactivarM = useMutation(
    accion(
      () => unwrap<AlumnoDetalle>(api.DELETE('/api/v1/admin/users/{id}/suspension', id)),
      'Suspensión levantada.',
    ),
  );
  const bloquearM = useMutation({
    mutationFn: (reason: string) =>
      unwrap<AlumnoDetalle>(
        api.POST('/api/v1/admin/users/{id}/block', { ...id, body: { reason } }),
      ),
    onSuccess: (d: AlumnoDetalle) => {
      refrescar(d);
      setBloquear(false);
      toast.success('Cuenta bloqueada.');
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  const desbloquearM = useMutation(
    accion(
      () => unwrap<AlumnoDetalle>(api.DELETE('/api/v1/admin/users/{id}/block', id)),
      'Bloqueo retirado.',
    ),
  );
  const invitarM = useMutation(
    accion(
      () => ensureOk(api.POST('/api/v1/admin/users/{id}/password-invite', id)),
      'Enviamos el enlace para definir la contraseña.',
    ),
  );
  const borrarM = useMutation({
    mutationFn: () => proteger(() => ensureOk(api.DELETE('/api/v1/admin/users/{id}', id))),
    onSuccess: () => {
      toast.success('Cuenta eliminada.');
      void navigate('/alumnos', { replace: true });
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });

  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <section className="panel-vidrio space-y-3">
        <h2 className="flex items-center gap-2 text-lg font-extrabold">
          <PauseCircle size={20} aria-hidden /> Suspensión
        </h2>
        {a.suspension ? (
          <>
            <p>
              Suspendida desde {fechaHora(a.suspension.createdAt)} por{' '}
              <strong>{a.suspension.reason}</strong>
              {a.suspension.amountDue && ` · adeudo ${mxn(a.suspension.amountDue)}`}
              {a.suspension.dueOn && ` · fecha límite ${fechaLarga(a.suspension.dueOn)}`}.
            </p>
            {editable && (
              <button
                type="button"
                className="btn-cs btn-exito"
                onClick={() => reactivarM.mutate()}
                disabled={reactivarM.isPending}
              >
                Levantar suspensión
              </button>
            )}
          </>
        ) : (
          <>
            <p className="text-texto-2">
              La cuenta no está suspendida. Suspenderla cierra sus sesiones y le impide entrar.
            </p>
            {editable && (
              <button
                type="button"
                className="btn-cs btn-contorno"
                onClick={() => setSuspender(true)}
              >
                Suspender
              </button>
            )}
          </>
        )}
      </section>

      <section className="panel-vidrio space-y-3">
        <h2 className="flex items-center gap-2 text-lg font-extrabold">
          <Ban size={20} aria-hidden /> Bloqueo (lista negra)
        </h2>
        {a.block ? (
          <>
            <p>
              Bloqueada desde {fechaHora(a.block.blockedAt)}
              {a.block.reason && (
                <>
                  {' '}
                  por <strong>{a.block.reason}</strong>
                </>
              )}
              .
            </p>
            {editable && (
              <button
                type="button"
                className="btn-cs btn-exito"
                onClick={() => desbloquearM.mutate()}
                disabled={desbloquearM.isPending}
              >
                Quitar bloqueo
              </button>
            )}
          </>
        ) : (
          <>
            <p className="text-texto-2">
              Para infracciones de las políticas de contenido (compartir cuenta, capturas).
            </p>
            {editable && (
              <button
                type="button"
                className="btn-cs btn-contorno"
                onClick={() => setBloquear(true)}
              >
                Bloquear
              </button>
            )}
          </>
        )}
      </section>

      {editable && (
        <section className="panel-vidrio space-y-3">
          <h2 className="flex items-center gap-2 text-lg font-extrabold">
            <KeyRound size={20} aria-hidden /> Contraseña
          </h2>
          <p className="text-texto-2">
            Envía al alumno un enlace de un solo uso para definir una contraseña nueva (vence en 7
            días).
          </p>
          <button
            type="button"
            className="btn-cs btn-contorno"
            onClick={() => invitarM.mutate()}
            disabled={invitarM.isPending}
          >
            Enviar enlace
          </button>
        </section>
      )}

      {editable && <BorrarHistorial a={a} />}

      {editable && (
        <section className="panel-vidrio space-y-3 border border-rojo-600/40">
          <h2 className="flex items-center gap-2 text-lg font-extrabold text-rojo-texto">
            <Trash2 size={20} aria-hidden /> Eliminar cuenta
          </h2>
          <p className="text-texto-2">
            Borra el perfil y todo el avance, y libera el correo: la persona podrá registrarse de
            nuevo con él como cuenta nueva. Las licencias y los pagos se quedan en la cuenta
            eliminada (no se transfieren). Para impedirle el acceso, usa la suspensión o el bloqueo.
            Te pediremos confirmar tu identidad.
          </p>
          <button type="button" className="btn-cs btn-peligro" onClick={() => setBorrar(true)}>
            Eliminar cuenta
          </button>
        </section>
      )}

      <Modal abierto={suspender} onCerrar={() => setSuspender(false)} titulo="Suspender cuenta">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            suspenderM.mutate();
          }}
        >
          <Campo etiqueta="Motivo">
            <input
              className="input-vidrio"
              list="motivos-suspension"
              value={susp.reason}
              onChange={(e) => setSusp((s) => ({ ...s, reason: e.target.value }))}
              required
              minLength={3}
              maxLength={60}
            />
            <datalist id="motivos-suspension">
              <option value="Falta de pago" />
              <option value="Uso indebido de la cuenta" />
              <option value="Solicitud del alumno" />
            </datalist>
          </Campo>
          <div className="grid grid-cols-2 gap-3">
            <Campo etiqueta="Adeudo (MXN, opcional)">
              <input
                className="input-vidrio"
                inputMode="decimal"
                value={susp.amountDue}
                onChange={(e) => setSusp((s) => ({ ...s, amountDue: e.target.value }))}
              />
            </Campo>
            <Campo etiqueta="Fecha límite (opcional)">
              <input
                type="date"
                className="input-vidrio"
                value={susp.dueOn}
                onChange={(e) => setSusp((s) => ({ ...s, dueOn: e.target.value }))}
              />
            </Campo>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 accent-[var(--color-azul-600)]"
              checked={susp.notifyByEmail}
              onChange={(e) => setSusp((s) => ({ ...s, notifyByEmail: e.target.checked }))}
            />
            Avisar al alumno por correo (incluye los datos de transferencia si hay adeudo)
          </label>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-cs btn-contorno"
              onClick={() => setSuspender(false)}
            >
              Cancelar
            </button>
            <button type="submit" className="btn-cs btn-peligro" disabled={suspenderM.isPending}>
              Suspender
            </button>
          </div>
        </form>
      </Modal>
      <ConfirmarAccion
        abierto={bloquear}
        onCerrar={() => setBloquear(false)}
        titulo="Bloquear cuenta"
        descripcion="Cierra sus sesiones y le impide entrar hasta que se quite el bloqueo."
        textoBoton="Bloquear"
        peligro
        pedirMotivo="Motivo"
        ocupado={bloquearM.isPending}
        onConfirmar={(m) => bloquearM.mutate(m)}
      />
      <ConfirmarAccion
        abierto={borrar}
        onCerrar={() => setBorrar(false)}
        titulo={`Eliminar la cuenta de ${a.name}`}
        descripcion={`Se borran el perfil y el avance, y el correo ${a.email} queda libre para un registro nuevo. Las licencias y los pagos se quedan en la cuenta eliminada: no pasan a la cuenta nueva. No se puede deshacer.`}
        textoBoton="Eliminar"
        peligro
        ocupado={borrarM.isPending}
        onConfirmar={() => borrarM.mutate()}
      />
      {dialogo}
    </div>
  );
}

// ------------------------------------------------------------------ página

function Ficha({ id }: { id: number }) {
  const q = useAlumno(id);
  const editable = useCan('alumnos', 'editar');
  const [params, setParams] = useSearchParams();
  const pestana = (
    PESTANAS.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'datos'
  ) as Pestana;

  if (q.isPending) return <Cargando />;
  if (q.isError) return <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />;
  const a = q.data;
  return (
    <>
      <Link to="/alumnos" className="mb-3 inline-flex items-center gap-1 text-sm font-semibold">
        <ArrowLeft size={16} aria-hidden /> Alumnos
      </Link>
      <Encabezado
        titulo={a.name}
        descripcion={`#${a.id} · ${a.email}${a.activeMemberships.length ? ` · ${a.activeMemberships.map((m) => m.name).join(', ')}` : ' · sin licencia vigente'}`}
        acciones={
          <div className="flex gap-1">
            {!a.isActive && <span className="insignia">Inactiva</span>}
            {a.suspended && <span className="insignia insignia-exclusivo">Suspendida</span>}
            {a.blocked && <span className="insignia insignia-exclusivo">Bloqueada</span>}
          </div>
        }
      />
      <div role="tablist" aria-label="Secciones de la ficha" className="mb-5 flex flex-wrap gap-2">
        {PESTANAS.map(([k, texto]) => (
          <button
            key={k}
            role="tab"
            type="button"
            aria-selected={pestana === k}
            className={cn('btn-cs', pestana === k ? 'btn-primario' : 'btn-vidrio')}
            onClick={() => setParams(k === 'datos' ? {} : { tab: k }, { replace: true })}
          >
            {texto}
            {k === 'licencias' && ` (${a.counts.memberships})`}
            {k === 'pagos' && ` (${a.counts.payments})`}
            {k === 'sesiones' && ` (${a.counts.activeSessions})`}
          </button>
        ))}
      </div>
      <div role="tabpanel">
        {pestana === 'datos' && <TabDatos key={a.email + a.name} a={a} editable={editable} />}
        {pestana === 'licencias' && <TabLicencias a={a} editable={editable} />}
        {pestana === 'pagos' && <TabPagos a={a} />}
        {pestana === 'sesiones' && <TabSesiones a={a} editable={editable} />}
        {pestana === 'cuenta' && <TabCuenta a={a} editable={editable} />}
      </div>
    </>
  );
}

export function FichaPage() {
  const { id } = useParams();
  return (
    <PermissionGate seccion="alumnos">
      <Ficha id={Number(id)} />
    </PermissionGate>
  );
}
